import React, { useEffect, useRef, useState } from "react";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import BottomNav from "../BottomNav";
import cn from "../../../utils/cn";
import { Pause, Play, Triangle } from "../../Icons";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useAuthContext } from "../../../context/auth";
import EventEmitter from "eventemitter3";
import useCountDown from "../../../hooks/useCountDown";
import animation from "../../animation.json";
import keep from "../../arrowRed.json";

import interpolate from "../../../utils/interpolate";
import Lottie from "react-lottie";
import useAudio from "../../../hooks/useAudio";

function Line() {
  return (
    <svg
      width="4"
      height="92"
      viewBox="0 0 4 92"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1="2"
        y1="-8.74228e-08"
        x2="2"
        y2="92"
        stroke="url(#paint0_linear_5264_1049)"
        stroke-width="4"
        stroke-dasharray="6 6"
      />
      <defs>
        <linearGradient
          id="paint0_linear_5264_1049"
          x1="0"
          y1="0"
          x2="-1.99976"
          y2="0.0217366"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#E3FAFF" />
          <stop offset="1" stop-color="#53CAF5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Fit({
  active,
  callback,
  number,
  round,
  remainingTime,
}: {
  number: number;
  round: number;
  active: boolean;
  callback: () => void;
  remainingTime: number;
}) {
  const time = 60;
  const count = Math.min(remainingTime, 60);
  const { did } = useAuthContext();
  const isCompleted = useRef(false);
  const hasPlayed30 = useRef(false);
  const hasPlayed40 = useRef(false);
  const hasPlayed50 = useRef(false);
  const hasPlayed3 = useRef(false);

  const play = useAudio("countdown.mp3");
  const play30 = useAudio("second30_delayed.mp3");
  const play40 = useAudio("second40_delayed.mp3");
  const play50 = useAudio("second50_delayed.mp3");

  const getProgressBarGradient = () => {
    if (count <= 30 && count > 20) {
      return "linear-gradient(270deg, #F6B731 0%, rgba(246, 183, 49, 0) 100%)";
    } else if (count <= 20 && count > (round === 6 && number === 3 ? -1 : 10)) {
      return "linear-gradient(270deg, #FF5757 0%, rgba(255, 87, 87, 0) 100%)";
    } else {
      return "linear-gradient(270deg, #53CAF5 0%, rgba(44, 218, 0, 0.00) 100%)";
    }
  };

  const getCircleGradient = () => {
    if (count <= 30 && count > 20) {
      return "linear-gradient(135deg, #FFF5E0 0%, #F6B731 100%)";
    } else if (count <= 20 && count > (round === 6 && number === 3 ? -1 : 10)) {
      return "linear-gradient(135deg, #FFEBEB 0%, #FF5757 100%)";
    } else {
      return "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)";
    }
  };

  const getHeartbeatAnimation = () => {
    if (count <= 30 && count > 20) {
      return "heartbeat-normal 1s ease-in-out infinite";
    } else if (count <= 20 && count > 10) {
      return "heartbeat-fast 0.6s ease-in-out infinite";
    } else {
      return "heartbeat-slow 1.5s ease-in-out infinite";
    }
  };

  useEffect(() => {
    if (active && !isCompleted.current) {
      if (count === 0) {
        callback();
        isCompleted.current = true;
      }
      if (count === 32 && !hasPlayed30.current) {
        did === "1" && play30();
        hasPlayed30.current = true;
      }
      if (count === 20 && !hasPlayed40.current) {
        did === "1" && play40();
        hasPlayed40.current = true;
      }
      if (count === 10 && !hasPlayed50.current) {
        did === "1" && play50();
        hasPlayed50.current = true;
      }
      if (count === 3 && !hasPlayed3.current) {
        did === "1" && play();
        hasPlayed3.current = true;
      }
    }

    // Reset flags when count increases (new round)
    if (count > 30) {
      hasPlayed30.current = false;
      hasPlayed40.current = false;
      hasPlayed50.current = false;
      hasPlayed3.current = false;
    }
  }, [active, count]);

  return (
    <div className="gap-[50px] flex items-center">
      <h1 className="text-[48px] font-bold text-white">{number}SET</h1>
      <div
        className="w-[1000px] h-[100px] rounded-[100px] flex items-center justify-center relative"
        style={{
          border: "4px solid transparent",
          backgroundImage: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
          backgroundOrigin: "border-box",
          // backgroundClip: "content-box, border-box",
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2">
          <Line />
        </div>
        <div className="absolute top-0 left-[66.6%] -translate-x-1/2">
          <Line />
        </div>
        <div className="absolute top-0 left-[83.2%] -translate-x-1/2">
          <Line />
        </div>
        <div className="w-full h-full bg-bg rounded-[100px] flex items-center">
          <div
            className="w-[984px] h-[84px] rounded-[84px] flex justify-end mx-2 relative"
            style={{
              backgroundImage: getProgressBarGradient(),
              transition: "all 0.5s ease",
              width: `${interpolate(((60 - count) / time) * 100)}%`,
              minWidth: "84px",
            }}
          >
            <div
              className="w-[84px] h-[84px] rounded-[100px]"
              style={{
                backgroundImage: getCircleGradient(),
                animation: getHeartbeatAnimation(),
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkoutType2({ round, last }: { round: number; last?: boolean }) {
  const {
    emitter,
    sendSetStep,
    step,
    sendStartCountDown,
    sendStopCountDown,
    did,
  } = useAuthContext();
  const [showNext, setShowNext] = useState(false);
  const playNext = useAudio("nextstation.mp3");
  const playKeep = useAudio("keep.mp3");

  const [activeNumber, setActiveNumber] = useState(1);
  const { count, reset, start, stop, stopped } = useCountDown(
    180,
    () => {
      if (last) {
        setTimeout(() => {
          sendSetStep(step + 1);
        }, 3000);
      } else {
        sendSetStep(step + 1);
      }
    },
    true
  );

  useEffect(() => {
    const currentSetIndex = Math.floor((180 - count) / 60);
    if (currentSetIndex !== activeNumber - 1 && currentSetIndex < 3) {
      setActiveNumber(currentSetIndex + 1);
    }
  }, [count]);

  useEffect(() => {
    if (count === 9) {
      setShowNext(true);
      setTimeout(() => {
        if (round === 6) {
          did === "1" && playKeep();
        } else {
          did === "1" && playNext();
        }
      }, 1000);
    }
  }, [count, round, activeNumber, last, did]);
  useEffect(() => {
    start();

    emitter.current.on("resume", () => {
      start();
    });
    emitter.current.on("pause", () => {
      stop();
    });
    return () => {
      emitter.current.off("resume");
      emitter.current.off("pause");
    };
  }, []);

  return (
    <LandscapeProgramLayout step={4}>
      <>
        <style>{`
          @keyframes heartbeat-slow {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes heartbeat-normal {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          @keyframes heartbeat-fast {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.15); }
          }
        `}</style>
        <div className="bg-bg w-full relative h-full flex">
          <div className="absolute left-[680px] top-[150px] flex gap-[35px]">
            <div className="text-[44px] font-extrabold leading-[70px] text-center bg-gradient-to-br from-[#E3FAFF] to-[#53CAF5] bg-clip-text text-transparent">
              Easy
            </div>
            <div className="text-[44px] font-extrabold leading-[70px] text-center bg-gradient-to-br from-[#FFF5E0] to-[#F6B731] bg-clip-text text-transparent">
              Normal
            </div>
            <div className="text-[44px] font-extrabold leading-[70px] text-center bg-gradient-to-br from-[#FFEBEB] to-[#FF5757] bg-clip-text text-transparent">
              Hard
            </div>
          </div>
          <div className="flex flex-col gap-[50px] ml-[59px] flex-1 justify-center">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <Fit
                  round={round}
                  active={i <= activeNumber - 1 && !stopped}
                  callback={() => {
                    if (i === 2) {
                      return;
                    } else {
                      setActiveNumber((prev) => prev + 1);
                    }
                  }}
                  number={i + 1}
                  key={i}
                  remainingTime={Math.max(0, count - 60 * (2 - i))}
                />
              ))}
          </div>
          <div className="absolute left-[1440px] top-1/2 -translate-y-1/2 flex flex-col items-center w-[420px]">
            <h2 className="text-[80px] font-bold text-white">{round}/6R</h2>
            <h2 className="text-[140px] font-bold text-white">
              {String(
                Math.floor(Math.max(0, count - 60 * (3 - activeNumber)) / 60)
              ).padStart(2, "0")}
              :
              {String(
                Math.max(0, count - 60 * (3 - activeNumber)) % 60
              ).padStart(2, "0")}{" "}
            </h2>
            <button
              onClick={() => {
                if (stopped) {
                  sendStartCountDown();
                } else {
                  sendStopCountDown();
                }
              }}
            >
              {stopped ? <Play /> : <Pause />}
            </button>
          </div>
          {showNext ? (
            round < 6 ? (
              <div className="absolute bottom-[100px] left-1/2 -translate-x-1/2 flex gap-[20px] items-center">
                <h1 className="text-[48px] font-extrabold gradient-text uppercase">
                  next station
                </h1>
                <Lottie
                  options={{
                    loop: true,
                    animationData: animation,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                  width={142}
                  height={66}
                  isClickToPauseDisabled
                />
              </div>
            ) : (
              <div className="absolute bottom-[100px] left-1/2 -translate-x-1/2 flex gap-[20px] items-center">
                <h1 className="text-[48px] font-extrabold gradient-text-red uppercase">
                  keep going
                </h1>
                <Lottie
                  options={{
                    loop: true,
                    animationData: keep,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                  width={142}
                  height={66}
                  isClickToPauseDisabled
                />
              </div>
            )
          ) : (
            <></>
          )}
        </div>
      </>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default WorkoutType2;
